import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Footer } from '../../components/Footer';
import './blog.css';

const THEME_KEY = 'revvly-blog-theme';

function LogoMark() {
  return (
    <svg viewBox="0 0 32 32" fill="none">
      <g transform="translate(4 6.5)">
        <path
          d="M1.021 10.5625C4.31511 10.5625 5.13864 2.78709 6.7857 1.15015C8.43276 -0.0775511 8.84453 7.6979 10.0798 13.8364C10.9033 17.5195 12.1386 17.9287 13.3739 15.4733C15.021 12.1995 16.2563 5.24249 17.9033 4.42402C19.1386 4.01479 20.3739 8.51636 22.021 8.51636"
          stroke="#082535"
          strokeWidth="2.04195"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

interface BlogLayoutProps {
  children: React.ReactNode;
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  } catch {
    return false;
  }
}

function getInitialBlogTheme(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    /* localStorage unavailable */
  }
  // No explicit choice yet — match the user's system (e.g. arriving from the
  // dark marketing site with the OS in dark mode).
  return systemPrefersDark() ? 'dark' : 'light';
}

export function useBlogTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(getInitialBlogTheme);

  const toggle = () => {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem(THEME_KEY, next); } catch {}
      return next;
    });
  };

  // Follow the system preference live, until the user makes an explicit choice.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return undefined;
    const onChange = (e: MediaQueryListEvent) => {
      try {
        if (localStorage.getItem(THEME_KEY)) return; // explicit choice wins
      } catch {
        /* localStorage unavailable */
      }
      setTheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return { theme, toggle };
}

export function BlogLayout({ children }: BlogLayoutProps) {
  const { theme, toggle } = useBlogTheme();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('blog-body');
    const prev = document.documentElement.style.scrollSnapType;
    document.documentElement.style.scrollSnapType = 'none';
    return () => {
      document.body.classList.remove('blog-body');
      document.documentElement.style.scrollSnapType = prev;
    };
  }, []);

  return (
    <div className="rv-blog" data-theme={theme === 'dark' ? 'dark' : undefined}>
      <div className="rv-shell">
        <nav className="rv-nav">
          <Link className="rv-brand" to="/">
            <span className="mark"><LogoMark /></span>
            Revvly
          </Link>
          <div className="rv-nav-right">
            <div className="rv-nav-links">
              <Link className="on" to="/blog">Blog</Link>
              <Link to="/glossary">Glossary</Link>
              <Link to="/docs">Docs</Link>
            </div>
            <button
              type="button"
              className="rv-theme-toggle"
              onClick={toggle}
              aria-label="Toggle theme"
              aria-pressed={theme === 'dark'}
            >
              <svg className="icon-moon" viewBox="0 0 24 24">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
              <svg className="icon-sun" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </svg>
            </button>
            <button className="rv-cta-btn" onClick={() => navigate('/early-access')}>
              Get early access
            </button>
          </div>
        </nav>

        {children}
      </div>
      <Footer />
    </div>
  );
}

export { LogoMark };

// ---- Shared primitives ----

interface MetaRowProps {
  author?: string;
  readTime: string;
  date: string;
}
export function MetaRow({ author, readTime, date }: MetaRowProps) {
  return (
    <div className="rv-meta">
      {author && <><span>{author}</span><span className="dot" /></>}
      <span>{readTime}</span>
      <span className="dot" />
      <span>{date}</span>
    </div>
  );
}

export function Chip({ label }: { label: string }) {
  return <span className="chip">{label}</span>;
}
